import React, { useState, useCallback, useRef } from "react";
import {
  BooleanInput,
  Create,
  FormDataConsumer,
  SimpleForm,
  SelectInput,
  TextInput,
  useInput,
  SaveButton,
  Toolbar,
  useCreate,
  useRedirect,
  useNotify,
} from "react-admin";
import Grid from "@material-ui/core/Grid";
import { useForm } from "react-final-form";
import {
  validateDomain,
  validateRequired,
  validateNumericRequired,
} from "../../utils/Validations";
import { makeStyles } from "@material-ui/core/styles";
import "./index.css";
import { CustomSelect } from "../Shared/SearchableSelect";
import { PlaformSelect, ParentStoreSelect } from "./ShopifyStoreEdit";
import {
  DEFAULT_CUSTOMIZATION,
  withCustomizationDefaults,
} from "./customizationDefaults";
import {
    FONT_FIELDS,
    FONT_OPTIONS,
    COLOR_FIELDS,
    BORDER_RADIUS_FIELDS,
    BOOLEAN_CONFIG_FIELDS,
    BorderRadiusInput,
    CollegiateBandsSync,
    validateMax255,
    validateHexColor,
    normalizeHexValue,
} from "./customConfigurationShared";
import {
  Card,
  CardContent,
  Collapse,
  Typography,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

const useStyles = makeStyles({
  container: {
    width: "100%",
  },
  restrictionsCard: {
    marginTop: "20px",
    width: "100%",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px",
    cursor: "pointer",
  },
  cardContent: {
    paddingTop: "0",
  },
  restrictionField: {
    marginBottom: "16px",
  },
  customConfigField: {
    marginBottom: "8px",
  },
  customConfigCard: {
    marginTop: "20px",
    width: "100%",
  },
  colorInputRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  colorSwatch: {
    width: "40px",
    height: "40px",
    border: "1px solid #d7d7d7",
    borderRadius: "4px",
    padding: "2px",
    background: "transparent",
    flexShrink: 0,
  },
  emptyColorSwatch: {
    width: "40px",
    height: "40px",
    border: "1px dashed #bdbdbd",
    borderRadius: "4px",
    padding: 0,
    background: "#f5f5f5",
    cursor: "pointer",
    flexShrink: 0,
  },
  hiddenColorInput: {
    position: "absolute",
    width: 0,
    height: 0,
    opacity: 0,
    pointerEvents: "none",
  },
  hexTextInput: {
    width: "100%",
    height: "40px",
    borderRadius: "4px",
    border: "1px solid #c4c4c4",
    padding: "0 12px",
    "&::placeholder": {
      color: "#9e9e9e",
      opacity: 1,
    },
  },
  helperError: {
    color: "#d32f2f",
    fontSize: "12px",
    marginTop: "4px",
  },
  customConfigLabel: {
    margin: "0 0 4px 0",
    fontSize: "12px",
    color: "#757575",
  },
  fieldLabelRow: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  infoIconButton: {
    padding: "2px",
    color: "#94a3b8",
    "&:hover": {
      color: "#64748b",
      backgroundColor: "rgba(15, 23, 42, 0.06)",
    },
  },
  modernTooltip: {
    backgroundColor: "#1e293b",
    color: "#f8fafc",
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: 1.5,
    padding: "8px 12px",
    borderRadius: "8px",
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.18)",
    maxWidth: "260px",
  },
  modernTooltipArrow: {
    color: "#1e293b",
  },
});

const FieldLabelWithTooltip = ({ label, info, classes }) => (
  <p className={`${classes.customConfigLabel} ${classes.fieldLabelRow}`}>
    <span>{label}</span>
    {info ? (
      <Tooltip
        title={info}
        arrow
        placement="top"
        enterDelay={200}
        leaveDelay={80}
        classes={{
          tooltip: classes.modernTooltip,
          arrow: classes.modernTooltipArrow,
        }}
      >
        <IconButton
          type="button"
          size="small"
          className={classes.infoIconButton}
          aria-label={`${label} info`}
          tabIndex={-1}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : null}
  </p>
);

const FontSelectInput = ({ source, label, info, classes }) => (
  <div className={classes.customConfigField}>
    <FieldLabelWithTooltip label={label} info={info} classes={classes} />
    <SelectInput
      source={source}
      label={false}
      helperText={false}
      margin="dense"
      validate={validateRequired}
      choices={FONT_OPTIONS.map((option) => ({
        id: option.value,
        name: option.label,
      }))}
      fullWidth
    />
  </div>
);

const HexColorInput = ({ source, label, info, classes, required = true }) => {
    const colorInputRef = useRef(null);
    const { input, meta } = useInput({
        source,
        validate: required
            ? [validateRequired, validateHexColor]
            : [(value) => (value ? validateHexColor(value) : undefined)],
    });

  const normalizedValue = normalizeHexValue(input.value);
  const hasValidColor = /^#[0-9A-F]{6}$/.test(normalizedValue);
  const hasError =
    (meta.touched || meta.submitFailed) && (meta.error || meta.submitError);

  const handleColorPick = (event) => {
    input.onChange(event.target.value.toUpperCase());
  };

  return (
    <div className={classes.customConfigField}>
      <FieldLabelWithTooltip label={label} info={info} classes={classes} />
      <div className={classes.colorInputRow}>
        {hasValidColor ? (
          <input
            type="color"
            value={normalizedValue}
            onChange={handleColorPick}
            className={classes.colorSwatch}
          />
        ) : (
          <>
            <button
              type="button"
              aria-label={`Pick ${label}`}
              className={classes.emptyColorSwatch}
              onClick={() => colorInputRef.current?.click()}
            />
            <input
              ref={colorInputRef}
              type="color"
              value="#FFFFFF"
              onChange={handleColorPick}
              className={classes.hiddenColorInput}
              tabIndex={-1}
            />
          </>
        )}
        <input
          type="text"
          value={normalizedValue}
          placeholder="e.g. #003053"
          className={classes.hexTextInput}
          onChange={(event) =>
            input.onChange(normalizeHexValue(event.target.value))
          }
          onBlur={input.onBlur}
          onFocus={input.onFocus}
        />
      </div>
      {hasError ? (
        <div className={classes.helperError}>
          {meta.error || meta.submitError}
        </div>
      ) : null}
    </div>
  );
};

const CustomConfigurationCard = ({ classes }) => {
  return (
    <Card className={classes.customConfigCard}>
      <CardContent>
        <Typography variant="h6" style={{ marginBottom: "16px" }}>
          Custom Configuration
        </Typography>
        <Grid container spacing={2}>
          {FONT_FIELDS.map((field) => (
            <Grid item xs={12} sm={6} key={field.source}>
              <FontSelectInput
                source={field.source}
                label={field.label}
                info={field.info}
                classes={classes}
              />
            </Grid>
          ))}
          {COLOR_FIELDS.map((field) => (
            <Grid item xs={12} sm={6} key={field.source}>
              <HexColorInput
                source={field.source}
                label={field.label}
                info={field.info}
                classes={classes}
              />
            </Grid>
          ))}
          {BORDER_RADIUS_FIELDS.map((field) => (
            <Grid item xs={12} sm={6} key={field.source}>
              <div className={classes.customConfigField}>
                <FieldLabelWithTooltip label={field.label} classes={classes} />
                <BorderRadiusInput
                  source={field.source}
                  label={false}
                  helperText={false}
                  fullWidth
                />
              </div>
            </Grid>
          ))}
          <Grid item xs={12} sm={6}>
            <div className={classes.customConfigField}>
              <FieldLabelWithTooltip label="Tab Title" classes={classes} />
              <TextInput
                source="customization.tabTitle"
                label={false}
                helperText={false}
                validate={validateMax255}
                fullWidth
              />
            </div>
          </Grid>
          {BOOLEAN_CONFIG_FIELDS.map((field) => (
            <Grid item xs={12} sm={6} key={field.source}>
              <BooleanInput
                source={field.source}
                label={field.label}
                defaultValue={false}
              />
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

// Multiselect component for restrictions
const RESTRICTION_ID_FIELDS = [
    "id",
    "sku_code",
    "machine_pattern_id",
    "width_id",
    "weight_id",
    "gemstone_id",
    "gemstone_arrangement_id",
    "inlay_id",
    "mga_color_option_id",
    "machine_pattern_color_option_id",
    "all_cerakote_option_id",
    "cerakote_color_id",
    "engraving_option_id",
    "finish_id",
    "profile_id",
];

const getRestrictionOptionId = (item) => {
    if (!item) return null;
    const isValidId = (value) =>
        value !== null &&
        value !== undefined &&
        value !== "" &&
        (typeof value === "number" ||
            (typeof value === "string" && value.trim() !== ""));

    for (const field of RESTRICTION_ID_FIELDS) {
        if (isValidId(item[field])) return item[field];
    }

    const fallback = Object.keys(item).find(
        (key) => key.endsWith("_id") && isValidId(item[key])
    );
    return fallback ? item[fallback] : null;
};

const normalizeRestrictionOptions = (payload) => {
    const data = Array.isArray(payload) ? payload : [];
    const seen = new Set();

    return data
        .map((item) => {
            const rawId = getRestrictionOptionId(item);
            if (rawId === null || rawId === undefined || rawId === "") return null;

            const numId = Number(rawId);
            const id =
                !Number.isNaN(numId) && Number.isFinite(numId) ? numId : String(rawId);
            const key = String(id);
            if (seen.has(key)) return null;
            seen.add(key);

            return {
                ...item,
                id,
                name: item.name || item.sku_code || String(id),
            };
        })
        .filter(Boolean);
};

const RestrictionMultiSelect = ({ source, label, resource, classes }) => (
    <div className={classes.restrictionField}>
        <CustomSelect
            isMulti
            closeMenuOnSelect={false}
            source={source}
            label={label}
            resource={resource}
            optionValue="id"
            optionLabel="name"
            pageSize={1000}
            transformOptions={normalizeRestrictionOptions}
        />
    </div>
);

// Restrictions Card Component
const RestrictionsCard = ({ classes }) => {
  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const genericRestrictions = [
    {
      source: "ring_material_restrictions",
      label: "Ring Materials",
      resource: "custom-pricing/ring-materials",
    },
    {
      source: "finish_restrictions",
      label: "Finishes",
      resource: "custom-pricing/finishes",
    },
  ];

  const signetRestrictions = [
    {
      source: "signet_inlay_restrictions",
      label: "Signet Inlays",
      resource: "custom-pricing/signet-inlays",
    },
  ];

  const mensBuilderRestrictions = [
    {
      source: "sleeve_restrictions",
      label: "Sleeves",
      resource: "custom-pricing/sleeves",
    },
    {
      source: "design_pattern_restrictions",
      label: "Design Patterns",
      resource: "custom-pricing/machine-patterns",
    },
    {
      source: "profile_restrictions",
      label: "Profiles",
      resource: "custom-pricing/profiles",
    },
    {
      source: "width_restrictions",
      label: "Width Options",
      resource: "custom-pricing/widths",
    },
    {
      source: "weight_restrictions",
      label: "Weight Options",
      resource: "custom-pricing/weights",
    },
    {
      source: "gemstone_arrangement_restrictions",
      label: "Gemstone Arrangements",
      resource: "custom-pricing/gemstone-arrangements",
    },
    {
      source: "gemstone_restrictions",
      label: "Gemstones",
      resource: "custom-pricing/gemstones",
    },
    {
      source: "inlay_restrictions",
      label: "Inlays",
      resource: "custom-pricing/inlays",
    },
    {
      source: "groove_type_restricitions",
      label: "Inlay Placements",
      resource: "custom-pricing/groove-types",
    },
    {
      source: "machine_pattern_color_option_restrictions",
      label: "Machine Pattern Color Options",
      resource: "custom-pricing/machine-pattern-color-options",
    },
    {
      source: "mga_color_option_restrictions",
      label: "MGA Color Options",
      resource: "custom-pricing/mga-color-options",
    },
    {
      source: "all_cerakote_restrictions",
      label: "All Cerakote",
      resource: "custom-pricing/all-cerakote-options",
    },
    {
      source: "engraving_option_restrictions",
      label: "Engraving Options",
      resource: "custom-pricing/engraving-fonts",
    },
  ];

  return (
    <Card className={classes.restrictionsCard}>
      <div className={classes.cardHeader} onClick={handleExpandClick}>
        <Typography variant="h6">Restrictions</Typography>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </div>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent className={classes.cardContent}>
          {/* Generic Section */}
          <Typography
            variant="h6"
            style={{ marginBottom: "16px", marginTop: "8px" }}
          >
            Generic
          </Typography>
          <Grid container spacing={2}>
            {genericRestrictions.map((restriction) => (
              <Grid item xs={12} sm={6} key={restriction.source}>
                <RestrictionMultiSelect
                  source={restriction.source}
                  label={restriction.label}
                  resource={restriction.resource}
                  classes={classes}
                />
              </Grid>
            ))}
          </Grid>

          {/* Signet Section */}
          <Typography
            variant="h6"
            style={{ marginBottom: "16px", marginTop: "24px" }}
          >
            Signet
          </Typography>
          <Grid container spacing={2}>
            {signetRestrictions.map((restriction) => (
              <Grid item xs={12} sm={6} key={restriction.source}>
                <RestrictionMultiSelect
                  source={restriction.source}
                  label={restriction.label}
                  resource={restriction.resource}
                  classes={classes}
                />
              </Grid>
            ))}
          </Grid>

          {/* Mens Builder Section */}
          <Typography
            variant="h6"
            style={{ marginBottom: "16px", marginTop: "24px" }}
          >
            Mens Builder
          </Typography>
          <Grid container spacing={2}>
            {mensBuilderRestrictions.map((restriction) => (
              <Grid item xs={12} sm={6} key={restriction.source}>
                <RestrictionMultiSelect
                  source={restriction.source}
                  label={restriction.label}
                  resource={restriction.resource}
                  classes={classes}
                />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Collapse>
    </Card>
  );
};

const ShopifyCreateSave = ({
  handleSubmitWithRedirect,
  resource,
  ...saveProps
}) => {
  const [create] = useCreate(resource);
  const redirectTo = useRedirect();
  const notify = useNotify();
  const { basePath } = saveProps;
  const form = useForm();

  const handleClick = useCallback(() => {
    const values = form.getState().values;
    create(
      {
        payload: { data: withCustomizationDefaults(values) },
      },
      {
        onSuccess: ({ data }) => {
          if (data?.redirect_url) {
            window.open(data.redirect_url, "_blank");
          }
          notify(data?.message ?? "Saved", 2000);
          redirectTo(basePath);
        },
        onFailure: ({ response }) => {
          const errors = response?.data?.errors ?? {};
          if ("message" in errors) {
            notify(errors.message, { type: "warning" });
            return;
          }
          const key = Object.keys(errors)[0];
          const message =
            key && errors[key]?.[0] ? errors[key][0] : "An error occurred";
          notify(message, { type: "warning" });
        },
      },
    );
  }, [form, create, notify, redirectTo, basePath]);

  return (
    <SaveButton
      {...saveProps}
      handleSubmitWithRedirect={handleClick}
      disabled={form.getState().hasValidationErrors}
    />
  );
};

const CreateToolbar = (props) => (
  <Toolbar {...props}>
    <ShopifyCreateSave redirect="list" submitOnEnter={false} variant="text" />
  </Toolbar>
);

export const ShopifyStoreCreate = (props) => {
  const classes = useStyles();

    return (
        <Create {...props} className="overflow-visible">
            <SimpleForm
                toolbar={<CreateToolbar />}
                initialValues={{ customization: DEFAULT_CUSTOMIZATION }}
            >
                <Grid
                    container
                    className={classes.container}
                    justify="space-between"
                >
                    <Grid item xs={5}>
                        <p className="label">Platform Name</p>
                        <PlaformSelect />
                    </Grid>
                    <FormDataConsumer>
                        {({ formData, ...rest }) => {
                            console.log("formData", formData);
                            if (formData.platform_name === "magento") {
                                return (
                                    <>
                                        <Grid item xs={5}>
                                            <TextInput
                                                type="number"
                                                source="attribute_set_id"
                                                label="Attribute Id"
                                                validate={validateRequired}
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={5}>
                                            <TextInput
                                                type="number"
                                                source="category_id"
                                                label="Category Id"
                                                fullWidth
                                            />
                                        </Grid>
                                    </>
                                );
                            }
                        }}
                    </FormDataConsumer>
                    <Grid item xs={5}>
                        <TextInput
                            source="store_name"
                            validate={validateRequired}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={5}>
                        <TextInput
                            source="keys.api_key"
                            label="Api key"
                            validate={validateRequired}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={5}>
                        <TextInput
                            source="keys.password"
                            label="Password"
                            validate={validateRequired}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={5}>
                        <TextInput
                            placeholder="weareemblem.myshopify.com/"
                            source="base_url"
                            label="Store url"
                            validate={[validateRequired, validateDomain]}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={5}>
                        <TextInput
                            placeholder="customize"
                            source="subpath"
                            label="Subpath"
                            validate={validateRequired}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={5}>
                        <TextInput
                            placeholder="USD / CAD / GBP"
                            source="currency"
                            label="Currency"
                            validate={validateRequired}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={5}>
                        <TextInput
                            type="number"
                            source="buffer_shipping_days"
                            label="Buffer Shipping Days"
                            validate={validateNumericRequired}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={5}>
                                <Tooltip
                                    title="If set to 0, Buffer Shipping Days will be applied by default for rush"
                                    arrow
                                    placement="top"
                                >
                                    <div>
                                        <TextInput
                                            type="number"
                                            source="rush_buffer_shipping_days"
                                            label="Rush Buffer Shipping Days"
                                            validate={validateNumericRequired}
                                            fullWidth
                                        />
                                    </div>
                                </Tooltip>
                            </Grid>
                    <Grid item xs={5}>
                        <TextInput
                            type="number"
                            source="rush_markup"
                            label="Rush Markup"
                            validate={validateNumericRequired}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={5}>
                        <p className="label">Store</p>
                        <ParentStoreSelect />
                    </Grid>
                    <Grid item xs={5}>
                        <HexColorInput
                            source="custom_swatch_hex"
                            label="Custom Swatch Hex"
                            classes={classes}
                            required={false}
                        />
                    </Grid>
                    <Grid item xs={5}>
                        <BooleanInput source="show_msrp" defaultValue={false}/>
                        <BooleanInput source="is_approved" label="Subscribed" />
                        <BooleanInput source="can_select_shipping_priority" label="Can Select Shipping Priority" />
                        <BooleanInput source="show_women_builder" defaultValue={false} />
                        <BooleanInput source="show_signet_builder" defaultValue={false} />
                        <BooleanInput source="show_collegiate_builder" defaultValue={false}/>
                        <BooleanInput source="show_collegiate_bands_builder" defaultValue={false}/>
                        <CollegiateBandsSync />
          </Grid>
        </Grid>
        <CustomConfigurationCard classes={classes} />
        <RestrictionsCard classes={classes} />
      </SimpleForm>
    </Create>
  );
};
